import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileText, Loader2, RefreshCw, Trash2 } from "lucide-react";
import { useBienPebPdfUpload } from "@/hooks/useBienPebPdfUpload";
import { cn } from "@/lib/utils";

interface PebPdfDropzoneProps {
  bienId: string;
  /** URL publique du PDF déposé ("" si aucun) — colonne biens.pebCertificatPdf. */
  value: string;
  /** Nouvelle URL après upload/remplacement, "" après suppression. */
  onChange: (url: string) => void;
  readOnly?: boolean;
}

/**
 * Dépôt du PDF du certificat PEB sur la fiche/le formulaire VENTE (maquette
 * mockup-fiche-vente.html, section Énergie / PEB). Un seul PDF par bien :
 * un nouvel upload remplace l'ancien fichier storage (useBienPebPdfUpload,
 * bucket peb-certificats — même mécanisme que le prestataire PEB).
 *
 * Le parent persiste l'URL immédiatement (le fichier est déjà au storage,
 * on n'attend pas le blur — même stratégie que PhotoGallery côté vente).
 */
export function PebPdfDropzone({ bienId, value, onChange, readOnly }: PebPdfDropzoneProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const { upload, remove, pathFromUrl, uploading } = useBienPebPdfUpload(bienId);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(file: File | null | undefined) {
    if (!file || uploading) return;
    setError(null);
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      setError(t("v2.missions.pebPdfOnly"));
      return;
    }
    try {
      const { url } = await upload(file, pathFromUrl(value));
      onChange(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function handleDelete() {
    if (!value || !confirm(t("vente.form.pebPdfDeleteConfirm"))) return;
    setError(null);
    const path = pathFromUrl(value);
    if (path) {
      try {
        await remove(path);
      } catch {
        /* best-effort : vider la colonne prime sur le fichier storage */
      }
    }
    onChange("");
  }

  const uploadingLabel = (
    <span className="inline-flex items-center gap-2">
      <Loader2 size={14} className="animate-spin" />
      {t("vente.form.pebPdfUploading")}
    </span>
  );

  return (
    <div className="flex flex-col">
      <div className="px-3 mb-1">
        <span className="text-xs font-medium tracking-wider uppercase text-text-muted">
          {t("vente.fields.pebCertificatPdf")}
        </span>
      </div>

      {!readOnly && (
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          onChange={(e) => {
            handleFile(e.target.files?.[0]);
            e.target.value = ""; // permet de re-choisir le même fichier
          }}
        />
      )}

      {value ? (
        /* PDF déposé : lien d'ouverture + remplacement/suppression */
        <div className="flex items-center gap-2.5 px-3 py-2 bg-surface border border-solid border-transparent rounded-md">
          <FileText size={16} className="text-accent flex-shrink-0" />
          <a
            href={value}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-text-primary underline underline-offset-2 hover:text-accent transition-colors truncate outline-none shadow-none"
          >
            {t("v2.missions.pebOpenPdf")}
          </a>
          {!readOnly && (
            <div className="ml-auto flex items-center gap-1 flex-shrink-0">
              {uploading ? (
                <span className="text-xs text-text-muted px-2">{uploadingLabel}</span>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-text-secondary rounded-md hover:text-accent hover:bg-surface-2 transition-colors cursor-pointer outline-none shadow-none"
                  >
                    <RefreshCw size={13} />
                    {t("vente.form.pebPdfReplace")}
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="p-1.5 rounded-md text-text-muted hover:text-danger hover:bg-danger/10 transition-colors cursor-pointer outline-none shadow-none"
                    aria-label={t("v2.missions.pebDeletePdf")}
                    title={t("v2.missions.pebDeletePdf")}
                  >
                    <Trash2 size={14} />
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      ) : readOnly ? (
        /* Lecture seule sans document (pattern ReadOnlyRow) */
        <div className="px-3 py-2 text-sm text-text-primary">—</div>
      ) : (
        /* Dropzone (cf. maquette : « Glissez le PDF … ou cliquez pour parcourir ») */
        <div
          role="button"
          tabIndex={0}
          onClick={() => !uploading && inputRef.current?.click()}
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === " ") && !uploading) inputRef.current?.click();
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFile(e.dataTransfer.files?.[0]);
          }}
          className={cn(
            "px-3 py-6 rounded-md bg-surface text-center text-sm border border-dashed",
            "transition-colors cursor-pointer outline-none shadow-none",
            dragOver
              ? "border-accent text-accent"
              : "border-border text-text-muted hover:border-border-hover",
            uploading && "cursor-default",
          )}
        >
          {uploading ? uploadingLabel : t("vente.form.pebDropzone")}
        </div>
      )}

      {error && <span className="text-xs text-danger mt-1 px-3">{error}</span>}
    </div>
  );
}
